import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./passionaAidsLogo.jpg";

const COLORS = {
    navy: "#031B35",
    deepNavy: "#021426",
    navyBlue: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",

    lightBlue: "#EDF7FC",
    checkBlue: "#5EA9D0",
    checkBlueBorder: "#B6D8EA",

    red: "#C62828",
    redLight: "#FDEEEE",
    redBorder: "#E2B5B5",

    grayDot: "#A7AFB8",

    text: "#172033",
    muted: "#687386",
    softText: "#7B8798",

    border: "#D8E0E9",
    borderDark: "#C5CFDA",

    white: "#FFFFFF",
    pageBg: "#FFFFFF",
    lightPanel: "#F7F9FB",

    pass: "#2E8B3C",
    passLight: "#EAF7EC",
    fail: "#C62828",
    failLight: "#FDEEEE",
};

const PASS_MARK = 50;
const DEFAULT_TOTAL_MARKS = 70;

const styles = StyleSheet.create({
    page: {
        paddingTop: 28,
        paddingBottom: 28,
        paddingHorizontal: 30,
        backgroundColor: COLORS.pageBg,
        fontFamily: "Helvetica",
        color: COLORS.text,
        fontSize: 9,
    },

    /* =========================
       HEADER
    ========================= */

    header: {
        borderBottomWidth: 2,
        borderBottomColor: COLORS.gold,
        paddingBottom: 10,
        marginBottom: 14,
    },

    headerRow: {
        flexDirection: "row",
        alignItems: "center",
    },

    logo: {
        width: 48,
        height: 48,
        objectFit: "cover",
        marginRight: 10,
    },

    headerText: {
        flex: 1,
    },

    schoolName: {
        fontSize: 15,
        fontWeight: "bold",
        color: COLORS.navy,
        letterSpacing: 0.5,
    },

    schoolSubtitle: {
        marginTop: 3,
        fontSize: 8,
        color: COLORS.muted,
    },

    paperLabel: {
        marginTop: 2,
        fontSize: 8,
        fontWeight: "bold",
        color: COLORS.blue,
    },

    applicationBadge: {
        paddingVertical: 5,
        paddingHorizontal: 8,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.lightPanel,
        alignItems: "center",
    },

    applicationBadgeLabel: {
        fontSize: 6.5,
        color: COLORS.muted,
        textTransform: "uppercase",
    },

    applicationBadgeValue: {
        marginTop: 2,
        fontSize: 8,
        fontWeight: "bold",
        color: COLORS.navy,
    },

    /* =========================
       APPLICANT INFORMATION
    ========================= */

    applicantPanel: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 6,
        backgroundColor: COLORS.lightPanel,
        padding: 9,
        marginBottom: 14,
    },

    applicantTitle: {
        fontSize: 8,
        fontWeight: "bold",
        color: COLORS.navy,
        textTransform: "uppercase",
        marginBottom: 7,
    },

    applicantGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
    },

    applicantCell: {
        width: "50%",
        marginBottom: 5,
        paddingRight: 8,
    },

    applicantLabel: {
        fontSize: 6.5,
        color: COLORS.softText,
        textTransform: "uppercase",
        marginBottom: 2,
    },

    applicantValue: {
        fontSize: 8.5,
        fontWeight: "bold",
        color: COLORS.text,
    },

    /* =========================
       SUMMARY
    ========================= */

    summaryTitle: {
        fontSize: 10,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 7,
    },

    summaryGrid: {
        flexDirection: "row",
        marginBottom: 13,
    },

    summaryCard: {
        flex: 1,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        padding: 8,
        marginRight: 6,
        backgroundColor: COLORS.white,
    },

    summaryCardLast: {
        marginRight: 0,
    },

    summaryLabel: {
        fontSize: 6.5,
        color: COLORS.muted,
        textTransform: "uppercase",
        marginBottom: 4,
    },

    summaryValue: {
        fontSize: 13,
        fontWeight: "bold",
        color: COLORS.navy,
    },

    summarySmall: {
        marginTop: 2,
        fontSize: 6.5,
        color: COLORS.softText,
    },

    statusBox: {
        paddingVertical: 9,
        paddingHorizontal: 10,
        borderRadius: 5,
        marginBottom: 14,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    statusBoxPass: {
        backgroundColor: COLORS.passLight,
        borderWidth: 1,
        borderColor: "#A8D5AE",
    },

    statusBoxFail: {
        backgroundColor: COLORS.failLight,
        borderWidth: 1,
        borderColor: COLORS.redBorder,
    },

    statusBoxIncomplete: {
        backgroundColor: COLORS.lightPanel,
        borderWidth: 1,
        borderColor: COLORS.border,
    },

    statusLabel: {
        fontSize: 7,
        color: COLORS.muted,
        textTransform: "uppercase",
    },

    statusValue: {
        marginTop: 2,
        fontSize: 12,
        fontWeight: "bold",
    },

    /* =========================
       SUBJECT HEADER
    ========================= */

    subjectHeader: {
        marginBottom: 10,
        paddingVertical: 9,
        paddingHorizontal: 10,
        borderRadius: 6,
        backgroundColor: COLORS.navy,
        flexDirection: "row",
        alignItems: "center",
    },

    subjectHeaderLeft: {
        flex: 1,
        minWidth: 0,
    },

    subjectHeaderTitle: {
        fontSize: 12,
        fontWeight: "bold",
        color: COLORS.white,
        letterSpacing: 0.4,
    },

    subjectHeaderSubtitle: {
        marginTop: 3,
        fontSize: 6.5,
        color: "#D9E4EF",
    },

    subjectHeaderApplicant: {
        width: 145,
        paddingHorizontal: 8,
        borderLeftWidth: 1,
        borderLeftColor: "#49647D",
    },

    subjectHeaderApplicantLabel: {
        fontSize: 6,
        color: "#B9C9D8",
        textTransform: "uppercase",
    },

    subjectHeaderApplicantName: {
        marginTop: 2,
        fontSize: 7.5,
        fontWeight: "bold",
        color: COLORS.white,
    },

    subjectScore: {
        width: 72,
        paddingLeft: 8,
        borderLeftWidth: 1,
        borderLeftColor: "#49647D",
        alignItems: "flex-end",
    },

    subjectScoreValue: {
        fontSize: 10,
        fontWeight: "bold",
        color: COLORS.gold,
    },

    subjectScoreLabel: {
        marginTop: 2,
        fontSize: 6,
        color: "#C9D6E2",
    },

    /* =========================
       QUESTION
    ========================= */

    questionCard: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        marginBottom: 7,
        padding: 8,
        backgroundColor: COLORS.white,
    },

    questionHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 6,
    },

    questionNumber: {
        width: 25,
        height: 18,
        borderRadius: 3,
        backgroundColor: COLORS.navyBlue,
        color: COLORS.white,
        fontSize: 7,
        fontWeight: "bold",
        textAlign: "center",
        paddingTop: 5,
        marginRight: 7,
    },

    questionText: {
        flex: 1,
        fontSize: 8.5,
        lineHeight: 1.35,
        color: COLORS.text,
        fontWeight: "bold",
    },

    sectionText: {
        marginBottom: 5,
        fontSize: 6.5,
        color: COLORS.blue,
        fontWeight: "bold",
        textTransform: "uppercase",
    },

    /* =========================
       OPTIONS - 2 COLUMN FLEX
    ========================= */

    optionsGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        marginTop: 2,
    },

    option: {
        width: "49%",
        flexDirection: "row",
        alignItems: "center",
        minHeight: 25,
        paddingVertical: 5,
        paddingHorizontal: 6,
        marginBottom: 4,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#E1E6EB",
        backgroundColor: COLORS.white,
    },

    optionSelectedCorrect: {
        backgroundColor: COLORS.lightBlue,
        borderColor: COLORS.checkBlueBorder,
    },

    optionSelectedWrong: {
        backgroundColor: COLORS.redLight,
        borderColor: COLORS.redBorder,
    },

    optionCorrectNotSelected: {
        backgroundColor: "#F7F8FA",
        borderColor: "#DDE2E7",
    },

    optionLetter: {
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: COLORS.navyBlue,
        color: COLORS.white,
        textAlign: "center",
        fontSize: 7,
        fontWeight: "bold",
        paddingTop: 5,
        marginRight: 5,
        flexShrink: 0,
    },

    optionText: {
        flex: 1,
        fontSize: 7.5,
        lineHeight: 1.3,
        color: COLORS.text,
    },

    /* =========================
       DRAWN CHECK MARK
       No Unicode ✓ required
    ========================= */

    checkIndicator: {
        width: 18,
        height: 18,
        marginLeft: 5,
        position: "relative",
        flexShrink: 0,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: COLORS.checkBlueBorder,
        backgroundColor: "#E8F5FB",
    },

    checkLineOne: {
        position: "absolute",
        width: 6,
        height: 2,
        backgroundColor: COLORS.checkBlue,
        left: 2,
        top: 9,
        transform: "rotate(45deg)",
    },

    checkLineTwo: {
        position: "absolute",
        width: 10,
        height: 2,
        backgroundColor: COLORS.checkBlue,
        left: 6,
        top: 7,
        transform: "rotate(-45deg)",
    },

    /* =========================
       RED / GRAY DOT
    ========================= */

    answerDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        marginLeft: 7,
        flexShrink: 0,
    },

    redDot: {
        backgroundColor: COLORS.red,
    },

    grayDot: {
        backgroundColor: COLORS.grayDot,
    },

    /* =========================
       LEGEND
    ========================= */

    legend: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        backgroundColor: COLORS.lightPanel,
        padding: 8,
        marginTop: 4,
        marginBottom: 12,
    },

    legendTitle: {
        fontSize: 7,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 6,
    },

    legendRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 4,
    },

    legendText: {
        marginLeft: 6,
        fontSize: 7,
        color: COLORS.muted,
    },

    legendCheckSpace: {
        width: 18,
        height: 18,
    },

    /* =========================
       FOOTER
    ========================= */

    footer: {
        position: "absolute",
        bottom: 12,
        left: 30,
        right: 30,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        paddingTop: 5,
        flexDirection: "row",
        justifyContent: "space-between",
    },

    footerText: {
        fontSize: 6,
        color: COLORS.softText,
    },

    pageNumber: {
        fontSize: 6,
        color: COLORS.softText,
    },

    emptyBox: {
        padding: 14,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        backgroundColor: COLORS.lightPanel,
        alignItems: "center",
    },

    emptyText: {
        fontSize: 8,
        color: COLORS.muted,
    },
});


/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
    if (value === null || value === undefined) {
        return fallback;
    }

    return String(value).trim() || fallback;
};

const numberValue = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value, decimals = 2) => {
    const number = numberValue(value, 0);

    return number.toFixed(decimals);
};

const formatDate = (value) => {
    if (!value) {
        return "—";
    }

    try {
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return safeString(value);
        }

        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return safeString(value);
    }
};

const formatTime = (value) => {
    if (!value) {
        return "—";
    }

    try {
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return safeString(value);
        }

        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return safeString(value);
    }
};

const getApplicationValue = (data, names, fallback = "—") => {
    for (const name of names) {
        const value = data?.[name];

        if (
            value !== null &&
            value !== undefined &&
            String(value).trim() !== ""
        ) {
            return value;
        }

        const applicationValue = data?.application?.[name];

        if (
            applicationValue !== null &&
            applicationValue !== undefined &&
            String(applicationValue).trim() !== ""
        ) {
            return applicationValue;
        }
    }

    return fallback;
};

const getApplicantName = (data) => {
    const directName = getApplicationValue(
        data,
        [
            "applicant_name",
            "applicantName",
            "full_name",
            "fullName",
            "name",
        ],
        ""
    );

    if (directName) {
        return safeString(directName);
    }

    const firstName = getApplicationValue(
        data,
        ["first_name", "firstName"],
        ""
    );

    const middleName = getApplicationValue(
        data,
        ["middle_name", "middleName"],
        ""
    );

    const lastName = getApplicationValue(
        data,
        ["last_name", "lastName"],
        ""
    );

    const combined = [firstName, middleName, lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

    if (combined) {
        return combined;
    }

    const applicant = data?.applicant;

    if (applicant) {
        const nestedCombined = [
            applicant.first_name,
            applicant.middle_name,
            applicant.last_name,
        ]
            .filter(Boolean)
            .join(" ")
            .trim();

        if (nestedCombined) {
            return nestedCombined;
        }

        if (applicant.name) {
            return safeString(applicant.name);
        }

        if (applicant.full_name) {
            return safeString(applicant.full_name);
        }
    }

    return "Applicant";
};


/* =========================================================
   ATTEMPT HELPERS
========================================================= */

const getAttemptAnswers = (attempt) => {
    if (!attempt) {
        return [];
    }

    const possibleArrays = [
        attempt.answers,
        attempt.answer_sheet,
        attempt.question_answers,
        attempt.attempt_answers,
        attempt.responses,
        attempt.results,
        attempt.data,
    ];

    for (const value of possibleArrays) {
        if (Array.isArray(value)) {
            return value;
        }

        if (
            value &&
            typeof value === "object" &&
            Array.isArray(value.answers)
        ) {
            return value.answers;
        }
    }

    return [];
};

const normalizeAttemptAnswers = (attempt) => {
    const answers = getAttemptAnswers(attempt);

    return answers
        .map((answer, index) => {
            const question = answer?.question || {};

            const questionId =
                answer?.question_id ??
                question?.id ??
                answer?.id ??
                index + 1;

            const questionNumber =
                answer?.question_number ??
                question?.question_number ??
                index + 1;

            const selected = String(
                answer?.selected_answer ??
                    answer?.selectedAnswer ??
                    ""
            )
                .trim()
                .toUpperCase();

            const correct = String(
                answer?.correct_answer ??
                    answer?.correctAnswer ??
                    question?.correct_answer ??
                    ""
            )
                .trim()
                .toUpperCase();

            const derivedIsCorrect =
                Boolean(selected) &&
                Boolean(correct) &&
                selected === correct;

            const optionsSource =
                answer?.options ||
                question?.options ||
                {};

            const options = {
                A:
                    optionsSource?.A ??
                    optionsSource?.a ??
                    question?.option_a ??
                    "",
                B:
                    optionsSource?.B ??
                    optionsSource?.b ??
                    question?.option_b ??
                    "",
                C:
                    optionsSource?.C ??
                    optionsSource?.c ??
                    question?.option_c ??
                    "",
                D:
                    optionsSource?.D ??
                    optionsSource?.d ??
                    question?.option_d ??
                    "",
            };

            return {
                questionId,
                questionNumber,
                section:
                    answer?.section ??
                    question?.section ??
                    "",

                questionText:
                    answer?.question_text ??
                    answer?.questionText ??
                    question?.question_text ??
                    question?.text ??
                    "",

                options,

                selectedAnswer: selected,
                correctAnswer: correct,

                isCorrect:
                    typeof answer?.is_correct === "boolean"
                        ? answer.is_correct
                        : derivedIsCorrect,

                marksAwarded: numberValue(
                    answer?.marks_awarded ??
                        answer?.marksAwarded ??
                        0
                ),

                marks: numberValue(
                    answer?.marks ??
                        question?.marks ??
                        1
                ),
            };
        })
        .sort(
            (a, b) =>
                numberValue(a.questionNumber) -
                numberValue(b.questionNumber)
        );
};

const getAttempt = (data, subject) => {
    if (!data) {
        return null;
    }

    const normalizedSubject = safeString(subject).toLowerCase();

    const directCandidates = [
        data?.[normalizedSubject],
        data?.results?.[normalizedSubject],
        data?.attempts?.[normalizedSubject],
        data?.exam_attempts?.[normalizedSubject],
    ];

    for (const candidate of directCandidates) {
        if (candidate) {
            return candidate;
        }
    }

    const arrays = [
        data?.attempts,
        data?.exam_attempts,
        data?.results,
        data?.examinations,
        data?.papers,
    ];

    for (const array of arrays) {
        if (!Array.isArray(array)) {
            continue;
        }

        const found = array.find((item) => {
            const itemSubject = String(
                item?.subject ??
                    item?.exam_subject ??
                    item?.exam?.subject ??
                    ""
            )
                .trim()
                .toLowerCase();

            return itemSubject === normalizedSubject;
        });

        if (found) {
            return found;
        }
    }

    return null;
};

const getAttemptScore = (attempt, questions) => {
    if (!attempt) {
        return 0;
    }

    const explicitScore =
        attempt.obtained_marks ??
        attempt.obtainedMarks ??
        attempt.score ??
        attempt.total_score ??
        attempt.totalScore;

    if (
        explicitScore !== null &&
        explicitScore !== undefined &&
        explicitScore !== ""
    ) {
        return numberValue(explicitScore);
    }

    return questions.reduce(
        (total, question) =>
            total + numberValue(question.marksAwarded),
        0
    );
};

const getAttemptTotalMarks = (attempt, questions) => {
    if (!attempt) {
        return questions.reduce(
            (total, question) => total + numberValue(question.marks),
            0
        );
    }

    const explicitTotal =
        attempt.total_marks ??
        attempt.totalMarks ??
        attempt.maximum_marks ??
        attempt.maximumMarks;

    if (
        explicitTotal !== null &&
        explicitTotal !== undefined &&
        explicitTotal !== ""
    ) {
        return numberValue(explicitTotal);
    }

    return questions.reduce(
        (total, question) => total + numberValue(question.marks),
        0
    );
};

const getQuestionStats = (questions) => {
    let correct = 0;
    let wrong = 0;
    let unanswered = 0;

    questions.forEach((question) => {
        const selected = safeString(
            question.selectedAnswer
        ).toUpperCase();

        const answerCorrect =
            question.isCorrect === true ||
            Boolean(
                selected &&
                    question.correctAnswer &&
                    selected ===
                        safeString(question.correctAnswer).toUpperCase()
            );

        if (!selected) {
            unanswered += 1;
        } else if (answerCorrect) {
            correct += 1;
        } else {
            wrong += 1;
        }
    });

    return {
        correct,
        wrong,
        unanswered,
    };
};


/* =========================================================
   DRAWN PDF CHECK MARK
========================================================= */

const PdfCheckMark = () => (
    <View style={styles.checkIndicator}>
        <View style={styles.checkLineOne} />
        <View style={styles.checkLineTwo} />
    </View>
);


/* =========================================================
   OPTION
========================================================= */

const PaperOption = ({
    optionKey,
    optionText,
    selected,
    correct,
}) => {
    const normalizedSelected = safeString(selected).toUpperCase();
    const normalizedCorrect = safeString(correct).toUpperCase();

    const isSelected = optionKey === normalizedSelected;
    const isCorrectOption = optionKey === normalizedCorrect;

    const selectedCorrect =
        isSelected && isCorrectOption;

    const selectedWrong =
        isSelected && !isCorrectOption;

    const correctNotSelected =
        !isSelected && isCorrectOption;

    let optionStyle = styles.option;

    if (selectedCorrect) {
        optionStyle = [
            styles.option,
            styles.optionSelectedCorrect,
        ];
    } else if (selectedWrong) {
        optionStyle = [
            styles.option,
            styles.optionSelectedWrong,
        ];
    } else if (correctNotSelected) {
        optionStyle = [
            styles.option,
            styles.optionCorrectNotSelected,
        ];
    }

    return (
        <View style={optionStyle}>
            <Text style={styles.optionLetter}>
                {optionKey}
            </Text>

            <Text style={styles.optionText}>
                {safeString(optionText, "—")}
            </Text>

            {selectedCorrect && <PdfCheckMark />}

            {selectedWrong && (
                <View
                    style={[
                        styles.answerDot,
                        styles.redDot,
                    ]}
                />
            )}

            {correctNotSelected && (
                <View
                    style={[
                        styles.answerDot,
                        styles.grayDot,
                    ]}
                />
            )}
        </View>
    );
};


/* =========================================================
   QUESTION CARD
========================================================= */

const QuestionCard = ({ question }) => {
    return (
        <View
            style={styles.questionCard}
            wrap={false}
        >
            {safeString(question.section) && (
                <Text style={styles.sectionText}>
                    {question.section}
                </Text>
            )}

            <View style={styles.questionHeader}>
                <Text style={styles.questionNumber}>
                    {question.questionNumber}
                </Text>

                <Text style={styles.questionText}>
                    {safeString(
                        question.questionText,
                        "Question text unavailable."
                    )}
                </Text>
            </View>

            <View style={styles.optionsGrid}>
                {["A", "B", "C", "D"].map(
                    (optionKey) => (
                        <PaperOption
                            key={optionKey}
                            optionKey={optionKey}
                            optionText={
                                question.options?.[
                                    optionKey
                                ]
                            }
                            selected={
                                question.selectedAnswer
                            }
                            correct={
                                question.correctAnswer
                            }
                        />
                    )
                )}
            </View>
        </View>
    );
};


/* =========================================================
   SUBJECT PAPER
========================================================= */

const SubjectPaper = ({
    title,
    attempt,
    applicantName,
}) => {
    const questions =
        normalizeAttemptAnswers(attempt);

    const score = getAttemptScore(
        attempt,
        questions
    );

    const totalMarks =
        getAttemptTotalMarks(
            attempt,
            questions
        );

    const stats =
        getQuestionStats(questions);

    const passed =
        score >= PASS_MARK;

    return (
        <Page
            size="A4"
            style={styles.page}
        >
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Image
                        src={schoolLogo}
                        style={styles.logo}
                    />

                    <View style={styles.headerText}>
                        <Text style={styles.schoolName}>
                            PASSION FOR ACADEMIC EXCELLENCE
                        </Text>

                        <Text style={styles.schoolSubtitle}>
                            Entrance Examination
                        </Text>

                        <Text style={styles.paperLabel}>
                            OFFICIAL EXAMINATION TEST PAPER
                        </Text>
                    </View>

                    <View
                        style={styles.applicationBadge}
                    >
                        <Text
                            style={
                                styles.applicationBadgeLabel
                            }
                        >
                            Application No.
                        </Text>

                        <Text
                            style={
                                styles.applicationBadgeValue
                            }
                        >
                            {safeString(
                                attempt?.application_number ??
                                    attempt?.applicationNumber ??
                                    "—"
                            )}
                        </Text>
                    </View>
                </View>
            </View>

            <View style={styles.subjectHeader}>
                <View
                    style={styles.subjectHeaderLeft}
                >
                    <Text
                        style={
                            styles.subjectHeaderTitle
                        }
                    >
                        {safeString(
                            title
                        ).toUpperCase()}
                    </Text>

                    <Text
                        style={
                            styles.subjectHeaderSubtitle
                        }
                    >
                        Entrance Examination •{" "}
                        {questions.length} Questions
                    </Text>
                </View>

                <View
                    style={
                        styles.subjectHeaderApplicant
                    }
                >
                    <Text
                        style={
                            styles.subjectHeaderApplicantLabel
                        }
                    >
                        Applicant
                    </Text>

                    <Text
                        style={
                            styles.subjectHeaderApplicantName
                        }
                    >
                        {safeString(
                            applicantName,
                            "Applicant"
                        )}
                    </Text>
                </View>

                <View style={styles.subjectScore}>
                    <Text
                        style={
                            styles.subjectScoreValue
                        }
                    >
                        {formatNumber(score)}/
                        {formatNumber(
                            totalMarks || DEFAULT_TOTAL_MARKS
                        )}
                    </Text>

                    <Text
                        style={
                            styles.subjectScoreLabel
                        }
                    >
                        {passed
                            ? "PASS"
                            : "BELOW PASS MARK"}
                    </Text>
                </View>
            </View>

            {questions.length === 0 ? (
                <View style={styles.emptyBox}>
                    <Text style={styles.emptyText}>
                        No examination questions or
                        answer records were found for
                        this subject.
                    </Text>
                </View>
            ) : (
                questions.map((question) => (
                    <QuestionCard
                        key={`${question.questionId}-${question.questionNumber}`}
                        question={question}
                    />
                ))
            )}

            <View style={styles.legend}>
                <Text style={styles.legendTitle}>
                    ANSWER KEY INDICATORS
                </Text>

                <View style={styles.legendRow}>
                    <PdfCheckMark />

                    <Text style={styles.legendText}>
                        Light-blue check = applicant
                        selected the correct answer
                    </Text>
                </View>

                <View style={styles.legendRow}>
                    <View
                        style={[
                            styles.answerDot,
                            styles.redDot,
                        ]}
                    />

                    <Text style={styles.legendText}>
                        Red dot = applicant selected
                        an incorrect answer
                    </Text>
                </View>

                <View style={styles.legendRow}>
                    <View
                        style={[
                            styles.answerDot,
                            styles.grayDot,
                        ]}
                    />

                    <Text style={styles.legendText}>
                        Gray dot = correct answer was
                        not selected
                    </Text>
                </View>
            </View>

            <View style={styles.footer}>
                <Text style={styles.footerText}>
                    PASSION FOR ACADEMIC EXCELLENCE •
                    Entrance Examination Paper
                </Text>

                <Text
                    style={styles.pageNumber}
                    render={({ pageNumber, totalPages }) =>
                        `Page ${pageNumber} of ${totalPages}`
                    }
                />
            </View>
        </Page>
    );
};


/* =========================================================
   MAIN PDF
========================================================= */

const EntrancePapperPDF = ({ data }) => {
    const applicantName =
        getApplicantName(data);

    const applicationNumber =
        getApplicationValue(
            data,
            [
                "application_number",
                "applicationNumber",
            ],
            "—"
        );

    const applicantEmail =
        getApplicationValue(
            data,
            ["email", "applicant_email"],
            "—"
        );

    const applicantPhone =
        getApplicationValue(
            data,
            [
                "phone",
                "phone_number",
                "telephone",
                "applicant_phone",
            ],
            "—"
        );

    const applicantGender =
        getApplicationValue(
            data,
            ["gender", "sex"],
            "—"
        );

    const mathematicsAttempt =
        getAttempt(
            data,
            "mathematics"
        );

    const englishAttempt =
        getAttempt(
            data,
            "english"
        );

    const mathematicsQuestions =
        normalizeAttemptAnswers(
            mathematicsAttempt
        );

    const englishQuestions =
        normalizeAttemptAnswers(
            englishAttempt
        );

    const mathematicsScore =
        getAttemptScore(
            mathematicsAttempt,
            mathematicsQuestions
        );

    const englishScore =
        getAttemptScore(
            englishAttempt,
            englishQuestions
        );

    const mathematicsTotal =
        getAttemptTotalMarks(
            mathematicsAttempt,
            mathematicsQuestions
        ) || DEFAULT_TOTAL_MARKS;

    const englishTotal =
        getAttemptTotalMarks(
            englishAttempt,
            englishQuestions
        ) || DEFAULT_TOTAL_MARKS;

    const backendAverage =
        data?.average_score ??
        data?.averageScore ??
        data?.final_average ??
        data?.finalAverage;

    const hasMathematics =
        Boolean(
            mathematicsAttempt &&
                mathematicsQuestions.length
        );

    const hasEnglish =
        Boolean(
            englishAttempt &&
                englishQuestions.length
        );

    const finalAverage =
        backendAverage !== null &&
        backendAverage !== undefined &&
        backendAverage !== ""
            ? numberValue(backendAverage)
            : hasMathematics && hasEnglish
            ? (mathematicsScore +
                  englishScore) /
              2
            : null;

    const finalTotal =
        data?.final_total ??
        data?.finalTotal ??
        DEFAULT_TOTAL_MARKS;

    const finalPassed =
        finalAverage !== null &&
        hasMathematics &&
        hasEnglish &&
        finalAverage >= PASS_MARK;

    const isComplete =
        hasMathematics &&
        hasEnglish;

    const mathematicsStats =
        getQuestionStats(
            mathematicsQuestions
        );

    const englishStats =
        getQuestionStats(
            englishQuestions
        );

    const totalCorrect =
        mathematicsStats.correct +
        englishStats.correct;

    const totalWrong =
        mathematicsStats.wrong +
        englishStats.wrong;

    const totalUnanswered =
        mathematicsStats.unanswered +
        englishStats.unanswered;

    const totalQuestions =
        mathematicsQuestions.length +
        englishQuestions.length;

    const completionStatus = isComplete
        ? finalPassed
            ? "PASSED"
            : "FAILED"
        : "INCOMPLETE";

    return (
        <Document
            title={`Entrance Examination Paper - ${applicantName}`}
            author="PASSION FOR ACADEMIC EXCELLENCE"
            subject="Entrance Examination Test Paper"
            creator="PASSION FOR ACADEMIC EXCELLENCE"
        >
            {/* =================================================
                SUMMARY PAGE
            ================================================= */}

            <Page
                size="A4"
                style={styles.page}
            >
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <Image
                            src={schoolLogo}
                            style={styles.logo}
                        />

                        <View style={styles.headerText}>
                            <Text
                                style={
                                    styles.schoolName
                                }
                            >
                                PASSION FOR ACADEMIC
                                EXCELLENCE
                            </Text>

                            <Text
                                style={
                                    styles.schoolSubtitle
                                }
                            >
                                Entrance Examination
                                Department
                            </Text>

                            <Text
                                style={
                                    styles.paperLabel
                                }
                            >
                                APPLICANT EXAMINATION
                                RECORD
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicationBadge
                            }
                        >
                            <Text
                                style={
                                    styles.applicationBadgeLabel
                                }
                            >
                                Application No.
                            </Text>

                            <Text
                                style={
                                    styles.applicationBadgeValue
                                }
                            >
                                {safeString(
                                    applicationNumber
                                )}
                            </Text>
                        </View>
                    </View>
                </View>

                <View
                    style={
                        styles.applicantPanel
                    }
                >
                    <Text
                        style={
                            styles.applicantTitle
                        }
                    >
                        Applicant Information
                    </Text>

                    <View
                        style={
                            styles.applicantGrid
                        }
                    >
                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Full Name
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {applicantName}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Application Number
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {safeString(
                                    applicationNumber
                                )}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Email
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {safeString(
                                    applicantEmail
                                )}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Phone
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {safeString(
                                    applicantPhone
                                )}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Gender
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {safeString(
                                    applicantGender
                                )}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.applicantCell
                            }
                        >
                            <Text
                                style={
                                    styles.applicantLabel
                                }
                            >
                                Examination Date
                            </Text>

                            <Text
                                style={
                                    styles.applicantValue
                                }
                            >
                                {formatDate(
                                    data?.created_at ??
                                        data?.createdAt ??
                                        mathematicsAttempt?.started_at ??
                                        mathematicsAttempt?.startedAt ??
                                        englishAttempt?.started_at ??
                                        englishAttempt?.startedAt
                                )}
                            </Text>
                        </View>
                    </View>
                </View>

                <Text style={styles.summaryTitle}>
                    Examination Summary
                </Text>

                <View style={styles.summaryGrid}>
                    <View style={styles.summaryCard}>
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Mathematics
                        </Text>

                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {formatNumber(
                                mathematicsScore
                            )}
                            /
                            {formatNumber(
                                mathematicsTotal
                            )}
                        </Text>

                        <Text
                            style={
                                styles.summarySmall
                            }
                        >
                            {mathematicsStats.correct}{" "}
                            correct •{" "}
                            {mathematicsStats.wrong}{" "}
                            wrong
                        </Text>
                    </View>

                    <View style={styles.summaryCard}>
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            English
                        </Text>

                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {formatNumber(
                                englishScore
                            )}
                            /
                            {formatNumber(
                                englishTotal
                            )}
                        </Text>

                        <Text
                            style={
                                styles.summarySmall
                            }
                        >
                            {englishStats.correct}{" "}
                            correct •{" "}
                            {englishStats.wrong}{" "}
                            wrong
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.summaryCard,
                            styles.summaryCardLast,
                        ]}
                    >
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Final Average
                        </Text>

                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {finalAverage !== null
                                ? `${formatNumber(
                                      finalAverage
                                  )}/${formatNumber(
                                      finalTotal
                                  )}`
                                : "—"}
                        </Text>

                        <Text
                            style={
                                styles.summarySmall
                            }
                        >
                            Pass mark:{" "}
                            {PASS_MARK}/
                            {finalTotal}
                        </Text>
                    </View>
                </View>

                <View
                    style={[
                        styles.statusBox,
                        !isComplete
                            ? styles.statusBoxIncomplete
                            : finalPassed
                            ? styles.statusBoxPass
                            : styles.statusBoxFail,
                    ]}
                >
                    <View>
                        <Text
                            style={
                                styles.statusLabel
                            }
                        >
                            Examination Result
                        </Text>

                        <Text
                            style={[
                                styles.statusValue,
                                {
                                    color: !isComplete
                                        ? COLORS.muted
                                        : finalPassed
                                        ? COLORS.pass
                                        : COLORS.fail,
                                },
                            ]}
                        >
                            {completionStatus}
                        </Text>
                    </View>

                    <View
                        style={{
                            alignItems: "flex-end",
                        }}
                    >
                        <Text
                            style={
                                styles.statusLabel
                            }
                        >
                            Raw Score
                        </Text>

                        <Text
                            style={[
                                styles.statusValue,
                                {
                                    color: COLORS.navy,
                                },
                            ]}
                        >
                            {finalAverage !== null
                                ? `${formatNumber(
                                      finalAverage
                                  )}/${formatNumber(
                                      finalTotal
                                  )}`
                                : "—"}
                        </Text>
                    </View>
                </View>

                <View style={styles.summaryGrid}>
                    <View style={styles.summaryCard}>
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Total Questions
                        </Text>

                        <Text
                            style={
                                styles.summaryValue
                            }
                        >
                            {totalQuestions}
                        </Text>
                    </View>

                    <View style={styles.summaryCard}>
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Correct
                        </Text>

                        <Text
                            style={[
                                styles.summaryValue,
                                {
                                    color: COLORS.pass,
                                },
                            ]}
                        >
                            {totalCorrect}
                        </Text>
                    </View>

                    <View style={styles.summaryCard}>
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Wrong
                        </Text>

                        <Text
                            style={[
                                styles.summaryValue,
                                {
                                    color: COLORS.fail,
                                },
                            ]}
                        >
                            {totalWrong}
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.summaryCard,
                            styles.summaryCardLast,
                        ]}
                    >
                        <Text
                            style={
                                styles.summaryLabel
                            }
                        >
                            Unanswered
                        </Text>

                        <Text
                            style={[
                                styles.summaryValue,
                                {
                                    color: COLORS.muted,
                                },
                            ]}
                        >
                            {totalUnanswered}
                        </Text>
                    </View>
                </View>

                <View style={styles.legend}>
                    <Text style={styles.legendTitle}>
                        HOW TO READ THE ANSWER PAPER
                    </Text>

                    <View style={styles.legendRow}>
                        <PdfCheckMark />

                        <Text style={styles.legendText}>
                            Light-blue check = applicant
                            selected the correct answer
                        </Text>
                    </View>

                    <View style={styles.legendRow}>
                        <View
                            style={[
                                styles.answerDot,
                                styles.redDot,
                            ]}
                        />

                        <Text style={styles.legendText}>
                            Red dot = applicant selected
                            an incorrect answer
                        </Text>
                    </View>

                    <View style={styles.legendRow}>
                        <View
                            style={[
                                styles.answerDot,
                                styles.grayDot,
                            ]}
                        />

                        <Text style={styles.legendText}>
                            Gray dot = correct answer
                            was not selected
                        </Text>
                    </View>
                </View>

                <View
                    style={{
                        marginTop: 4,
                        padding: 9,
                        borderWidth: 1,
                        borderColor: COLORS.border,
                        borderRadius: 5,
                    }}
                >
                    <Text
                        style={{
                            fontSize: 7,
                            fontWeight: "bold",
                            color: COLORS.navy,
                            marginBottom: 4,
                        }}
                    >
                        PASSING STANDARD
                    </Text>

                    <Text
                        style={{
                            fontSize: 7,
                            lineHeight: 1.4,
                            color: COLORS.muted,
                        }}
                    >
                        An applicant must obtain at least{" "}
                        {PASS_MARK}/{finalTotal} in the final
                        average to pass the entrance
                        examination.
                    </Text>
                </View>

                <View style={styles.footer}>
                    <Text style={styles.footerText}>
                        PASSION FOR ACADEMIC EXCELLENCE •
                        Official Examination Record
                    </Text>

                    <Text
                        style={styles.pageNumber}
                        render={({
                            pageNumber,
                            totalPages,
                        }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>

            {/* =================================================
                MATHEMATICS
            ================================================= */}

            {mathematicsAttempt && (
                <SubjectPaper
                    title="Mathematics"
                    attempt={mathematicsAttempt}
                    applicantName={applicantName}
                />
            )}

            {/* =================================================
                ENGLISH
            ================================================= */}

            {englishAttempt && (
                <SubjectPaper
                    title="English"
                    attempt={englishAttempt}
                    applicantName={applicantName}
                />
            )}
        </Document>
    );
};

export default EntrancePapperPDF;






// import React from "react";
// import {
//     Document,
//     Page,
//     Text,
//     View,
//     Image,
//     StyleSheet,
// } from "@react-pdf/renderer";

// import schoolLogo from "./passionaAidsLogo.jpg";

// const COLORS = {
//     navy: "#031B35",
//     deepNavy: "#021426",
//     navyBlue: "#062A4B",
//     blue: "#063A6B",
//     gold: "#F6C944",
//     orange: "#C0750D",

//     green: "#2E8B3C",
//     greenLight: "#EAF7EC",
//     greenBorder: "#A8D5AE",

//     red: "#C62828",
//     redLight: "#FDEEEE",
//     redBorder: "#E2B5B5",

//     grayDot: "#A7AFB8",

//     text: "#172033",
//     muted: "#687386",
//     softText: "#7B8798",

//     border: "#D8E0E9",
//     borderDark: "#C5CFDA",
//     white: "#FFFFFF",
//     pageBg: "#FFFFFF",

//     lightPanel: "#F7F9FB",
// };

// const PASS_MARK = 50;
// const DEFAULT_TOTAL_MARKS = 70;

// const styles = StyleSheet.create({
//     page: {
//         position: "relative",
//         paddingTop: 34,
//         paddingBottom: 54,
//         paddingHorizontal: 38,
//         backgroundColor: COLORS.pageBg,
//         color: COLORS.text,
//         fontFamily: "Helvetica",
//     },

//     header: {
//         borderBottomWidth: 2,
//         borderBottomColor: COLORS.navy,
//         paddingBottom: 12,
//         marginBottom: 14,
//     },

//     headerAccent: {
//         height: 4,
//         width: 100,
//         backgroundColor: COLORS.gold,
//         marginBottom: 12,
//     },

//     headerTop: {
//         flexDirection: "row",
//         alignItems: "center",
//     },

//     logoWrap: {
//         width: 64,
//         height: 64,
//         borderWidth: 1,
//         borderColor: COLORS.border,
//         borderRadius: 6,
//         alignItems: "center",
//         justifyContent: "center",
//         backgroundColor: COLORS.white,
//         marginRight: 13,
//     },

//     logo: {
//         width: 54,
//         height: 54,
//         objectFit: "contain",
//     },

//     schoolBlock: {
//         flex: 1,
//     },

//     schoolName: {
//         color: COLORS.navy,
//         fontSize: 17,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.6,
//     },

//     schoolSubtitle: {
//         marginTop: 4,
//         color: COLORS.orange,
//         fontSize: 7.5,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 1.2,
//     },

//     schoolContact: {
//         marginTop: 5,
//         color: COLORS.muted,
//         fontSize: 7,
//         lineHeight: 1.45,
//     },

//     documentBadge: {
//         width: 104,
//         paddingVertical: 8,
//         paddingHorizontal: 7,
//         borderWidth: 1,
//         borderColor: COLORS.borderDark,
//         borderRadius: 5,
//         backgroundColor: "#F7F9FB",
//         alignItems: "center",
//         justifyContent: "center",
//     },

//     documentBadgeTop: {
//         color: COLORS.muted,
//         fontSize: 5.8,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.8,
//     },

//     documentBadgeMain: {
//         marginTop: 4,
//         color: COLORS.navy,
//         fontSize: 8,
//         fontWeight: "bold",
//         textAlign: "center",
//         textTransform: "uppercase",
//     },

//     documentTitle: {
//         marginTop: 13,
//         textAlign: "center",
//         color: COLORS.navy,
//         fontSize: 16,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 1,
//     },

//     documentSubtitle: {
//         marginTop: 4,
//         textAlign: "center",
//         color: COLORS.muted,
//         fontSize: 7.5,
//     },

//     informationHeader: {
//         flexDirection: "row",
//         alignItems: "center",
//         marginBottom: 7,
//     },

//     informationHeaderLine: {
//         flex: 1,
//         height: 1,
//         backgroundColor: COLORS.border,
//     },

//     informationHeaderText: {
//         marginHorizontal: 8,
//         color: COLORS.navy,
//         fontSize: 7,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.8,
//     },

//     applicantBox: {
//         borderWidth: 1,
//         borderColor: COLORS.border,
//         borderRadius: 6,
//         backgroundColor: "#FBFCFD",
//         overflow: "hidden",
//     },

//     applicantTopBand: {
//         height: 5,
//         backgroundColor: COLORS.navy,
//     },

//     applicantGrid: {
//         flexDirection: "row",
//         flexWrap: "wrap",
//         padding: 11,
//     },

//     applicantItem: {
//         width: "33.333%",
//         marginBottom: 8,
//         paddingRight: 8,
//     },

//     applicantLabel: {
//         color: COLORS.softText,
//         fontSize: 5.8,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.5,
//     },

//     applicantValue: {
//         marginTop: 3,
//         color: COLORS.navy,
//         fontSize: 8,
//         fontWeight: "bold",
//     },

//     performanceArea: {
//         marginTop: 14,
//         marginBottom: 15,
//     },

//     performanceTitle: {
//         color: COLORS.navy,
//         fontSize: 8,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.7,
//         marginBottom: 8,
//     },

//     performanceRow: {
//         flexDirection: "row",
//         alignItems: "stretch",
//     },

//     scorePanel: {
//         width: 126,
//         padding: 10,
//         borderWidth: 1,
//         borderColor: COLORS.blue,
//         borderRadius: 7,
//         backgroundColor: "#F4F9FD",
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 8,
//     },

//     scoreCircleOuter: {
//         width: 86,
//         height: 86,
//         borderRadius: 43,
//         borderWidth: 4,
//         borderColor: COLORS.blue,
//         alignItems: "center",
//         justifyContent: "center",
//         backgroundColor: COLORS.white,
//     },

//     scoreCircleInner: {
//         width: 70,
//         height: 70,
//         borderRadius: 35,
//         borderWidth: 1,
//         borderColor: "#BFD9EA",
//         alignItems: "center",
//         justifyContent: "center",
//         backgroundColor: "#F8FCFF",
//     },

//     scoreNumberRow: {
//         flexDirection: "row",
//         alignItems: "baseline",
//         justifyContent: "center",
//     },

//     scoreNumberPassed: {
//         color: COLORS.green,
//         fontSize: 19,
//         fontWeight: "bold",
//     },

//     scoreNumberFailed: {
//         color: COLORS.red,
//         fontSize: 19,
//         fontWeight: "bold",
//     },

//     scoreSlash: {
//         color: COLORS.blue,
//         fontSize: 16,
//         fontWeight: "bold",
//         marginHorizontal: 2,
//     },

//     scoreTotal: {
//         color: COLORS.blue,
//         fontSize: 17,
//         fontWeight: "bold",
//     },

//     scoreOf: {
//         color: COLORS.muted,
//         fontSize: 6,
//         marginTop: 2,
//         textTransform: "uppercase",
//         letterSpacing: 0.4,
//     },

//     scoreCaption: {
//         marginTop: 6,
//         color: COLORS.navy,
//         fontSize: 6,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.6,
//         textAlign: "center",
//     },

//     scorePanels: {
//         flex: 1,
//         flexDirection: "row",
//         flexWrap: "wrap",
//     },

//     metricCard: {
//         width: "50%",
//         padding: 9,
//         borderWidth: 1,
//         borderColor: COLORS.border,
//         backgroundColor: COLORS.white,
//     },

//     metricCardTopLeft: {
//         borderTopLeftRadius: 6,
//     },

//     metricCardTopRight: {
//         borderTopRightRadius: 6,
//     },

//     metricCardBottomLeft: {
//         borderBottomLeftRadius: 6,
//     },

//     metricCardBottomRight: {
//         borderBottomRightRadius: 6,
//     },

//     metricLabel: {
//         color: COLORS.muted,
//         fontSize: 5.8,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.4,
//     },

//     metricValue: {
//         marginTop: 4,
//         color: COLORS.navy,
//         fontSize: 12,
//         fontWeight: "bold",
//     },

//     statusPanel: {
//         marginTop: 10,
//         padding: 10,
//         borderWidth: 1,
//         borderRadius: 6,
//         flexDirection: "row",
//         alignItems: "center",
//     },

//     statusPassed: {
//         backgroundColor: COLORS.greenLight,
//         borderColor: COLORS.greenBorder,
//     },

//     statusFailed: {
//         backgroundColor: COLORS.redLight,
//         borderColor: COLORS.redBorder,
//     },

//     statusIcon: {
//         width: 28,
//         height: 28,
//         borderRadius: 14,
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 9,
//     },

//     statusIconPassed: {
//         backgroundColor: COLORS.green,
//     },

//     statusIconFailed: {
//         backgroundColor: COLORS.red,
//     },

//     statusIconText: {
//         color: COLORS.white,
//         fontSize: 11,
//         fontWeight: "bold",
//     },

//     statusContent: {
//         flex: 1,
//     },

//     statusTitle: {
//         fontSize: 9,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.5,
//     },

//     statusDescription: {
//         marginTop: 3,
//         fontSize: 7,
//         lineHeight: 1.35,
//     },

//     sectionHeading: {
//         marginTop: 4,
//         marginBottom: 10,
//         flexDirection: "row",
//         alignItems: "center",
//     },

//     sectionHeadingNumber: {
//         width: 22,
//         height: 22,
//         borderRadius: 11,
//         backgroundColor: COLORS.navy,
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 7,
//     },

//     sectionHeadingNumberText: {
//         color: COLORS.white,
//         fontSize: 7,
//         fontWeight: "bold",
//     },

//     sectionHeadingTextWrap: {
//         flex: 1,
//     },

//     sectionHeadingTitle: {
//         color: COLORS.navy,
//         fontSize: 10,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.5,
//     },

//     sectionHeadingSubtitle: {
//         marginTop: 2,
//         color: COLORS.muted,
//         fontSize: 6.5,
//     },

//     sectionHeadingLine: {
//         width: 70,
//         height: 2,
//         backgroundColor: COLORS.gold,
//     },

//     subjectHeader: {
//         marginBottom: 10,
//         padding: 10,
//         borderRadius: 6,
//         backgroundColor: COLORS.navy,
//         flexDirection: "row",
//         alignItems: "center",
//         justifyContent: "space-between",
//     },

//     subjectHeaderLeft: {
//         flex: 1,
//     },

//     subjectHeaderTitle: {
//         color: COLORS.white,
//         fontSize: 11,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.7,
//     },

//     subjectHeaderSubtitle: {
//         marginTop: 3,
//         color: "#D7E4EF",
//         fontSize: 6.5,
//     },

//     subjectScore: {
//         color: COLORS.gold,
//         fontSize: 12,
//         fontWeight: "bold",
//     },

//     questionCard: {
//         marginBottom: 9,
//         padding: 9,
//         borderWidth: 1,
//         borderColor: COLORS.border,
//         borderRadius: 6,
//         backgroundColor: COLORS.white,
//     },

//     questionTop: {
//         flexDirection: "row",
//         alignItems: "center",
//         justifyContent: "space-between",
//         marginBottom: 7,
//     },

//     questionNumberWrap: {
//         flexDirection: "row",
//         alignItems: "center",
//         flex: 1,
//     },

//     questionNumberBadge: {
//         minWidth: 27,
//         height: 20,
//         paddingHorizontal: 5,
//         borderRadius: 4,
//         backgroundColor: COLORS.navy,
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 7,
//     },

//     questionNumberText: {
//         color: COLORS.white,
//         fontSize: 6.5,
//         fontWeight: "bold",
//     },

//     questionSection: {
//         color: COLORS.orange,
//         fontSize: 6,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.5,
//         flex: 1,
//     },

//     questionMarks: {
//         color: COLORS.muted,
//         fontSize: 6.5,
//         fontWeight: "bold",
//     },

//     questionText: {
//         color: COLORS.text,
//         fontSize: 8.2,
//         lineHeight: 1.45,
//         fontWeight: "bold",
//         marginBottom: 8,
//     },

//     option: {
//         flexDirection: "row",
//         alignItems: "center",
//         padding: 6,
//         marginBottom: 4,
//         borderRadius: 4,
//         borderWidth: 1,
//         borderColor: "#E1E6EB",
//         backgroundColor: COLORS.white,
//     },

//     optionSelectedCorrect: {
//         backgroundColor: COLORS.greenLight,
//         borderColor: COLORS.greenBorder,
//     },

//     optionSelectedWrong: {
//         backgroundColor: COLORS.redLight,
//         borderColor: COLORS.redBorder,
//     },

//     optionCorrectNotSelected: {
//         backgroundColor: "#F8F9FA",
//         borderColor: "#D8DDE2",
//     },

//     optionLetterCircle: {
//         width: 19,
//         height: 19,
//         borderRadius: 9.5,
//         borderWidth: 1,
//         borderColor: "#B8C1CB",
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 7,
//         flexShrink: 0,
//     },

//     optionLetterCircleSelectedCorrect: {
//         backgroundColor: COLORS.green,
//         borderColor: COLORS.green,
//     },

//     optionLetterCircleSelectedWrong: {
//         backgroundColor: COLORS.red,
//         borderColor: COLORS.red,
//     },

//     optionLetterCircleCorrectNotSelected: {
//         backgroundColor: "#EEF0F2",
//         borderColor: "#C6CCD2",
//     },

//     optionLetter: {
//         color: COLORS.muted,
//         fontSize: 6,
//         fontWeight: "bold",
//     },

//     optionLetterWhite: {
//         color: COLORS.white,
//     },

//     optionText: {
//         flex: 1,
//         color: COLORS.text,
//         fontSize: 7.2,
//         lineHeight: 1.4,
//     },

//     indicatorWrap: {
//         width: 20,
//         marginLeft: 7,
//         alignItems: "center",
//         justifyContent: "center",
//     },

//     checkIndicator: {
//         color: COLORS.green,
//         fontSize: 13,
//         fontWeight: "bold",
//     },

//     dotIndicator: {
//         width: 7,
//         height: 7,
//         borderRadius: 3.5,
//     },

//     redDot: {
//         backgroundColor: COLORS.red,
//     },

//     grayDot: {
//         backgroundColor: COLORS.grayDot,
//     },

//     questionResultBar: {
//         marginTop: 7,
//         paddingTop: 6,
//         borderTopWidth: 1,
//         borderTopColor: "#DCE2E8",
//         flexDirection: "row",
//         alignItems: "center",
//         justifyContent: "space-between",
//     },

//     questionResultText: {
//         color: COLORS.muted,
//         fontSize: 6,
//     },

//     questionResultStrong: {
//         color: COLORS.navy,
//         fontWeight: "bold",
//     },

//     resultChip: {
//         paddingHorizontal: 7,
//         paddingVertical: 3,
//         borderRadius: 10,
//         borderWidth: 1,
//     },

//     resultChipCorrect: {
//         backgroundColor: COLORS.greenLight,
//         borderColor: COLORS.greenBorder,
//     },

//     resultChipWrong: {
//         backgroundColor: COLORS.redLight,
//         borderColor: COLORS.redBorder,
//     },

//     resultChipUnanswered: {
//         backgroundColor: "#F1F3F5",
//         borderColor: "#D4D9DE",
//     },

//     resultChipText: {
//         fontSize: 5.5,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//     },

//     legendBox: {
//         marginTop: 12,
//         padding: 10,
//         borderWidth: 1,
//         borderColor: COLORS.border,
//         borderRadius: 6,
//         backgroundColor: "#FAFBFC",
//     },

//     legendTitle: {
//         color: COLORS.navy,
//         fontSize: 7,
//         fontWeight: "bold",
//         textTransform: "uppercase",
//         letterSpacing: 0.5,
//         marginBottom: 7,
//     },

//     legendRow: {
//         flexDirection: "row",
//         alignItems: "center",
//         marginBottom: 5,
//     },

//     legendCheck: {
//         width: 18,
//         alignItems: "center",
//         justifyContent: "center",
//         marginRight: 5,
//     },

//     legendDot: {
//         width: 8,
//         height: 8,
//         borderRadius: 4,
//         marginRight: 10,
//     },

//     legendText: {
//         color: COLORS.muted,
//         fontSize: 6.2,
//     },

//     watermark: {
//         position: "absolute",
//         left: 50,
//         right: 50,
//         bottom: 105,
//         textAlign: "center",
//         fontSize: 29,
//         fontWeight: "bold",
//         letterSpacing: 1,
//         opacity: 0.055,
//         transform: "rotate(-25deg)",
//     },

//     pageFooter: {
//         position: "absolute",
//         left: 38,
//         right: 38,
//         bottom: 18,
//         paddingTop: 6,
//         borderTopWidth: 1,
//         borderTopColor: COLORS.border,
//         flexDirection: "row",
//         alignItems: "center",
//         justifyContent: "space-between",
//     },

//     pageFooterText: {
//         color: COLORS.muted,
//         fontSize: 5.8,
//     },

//     pageFooterCenter: {
//         color: COLORS.muted,
//         fontSize: 5.8,
//         textAlign: "center",
//     },
// });

// const safeString = (value, fallback = "—") => {
//     if (
//         value === null ||
//         value === undefined ||
//         String(value).trim() === ""
//     ) {
//         return fallback;
//     }

//     return String(value);
// };

// const numberValue = (value, fallback = 0) => {
//     const number = Number(value);

//     return Number.isFinite(number)
//         ? number
//         : fallback;
// };

// const formatNumber = (value, decimals = 0) => {
//     const number = Number(value);

//     if (!Number.isFinite(number)) {
//         return decimals > 0 ? "0.00" : "0";
//     }

//     return number.toFixed(decimals);
// };

// const formatDate = (value) => {
//     if (!value) {
//         return "—";
//     }

//     const date = new Date(value);

//     if (Number.isNaN(date.getTime())) {
//         return safeString(value);
//     }

//     return date.toLocaleDateString("en-LR", {
//         year: "numeric",
//         month: "long",
//         day: "numeric",
//     });
// };

// const formatTime = (value) => {
//     if (!value) {
//         return "—";
//     }

//     const date = new Date(value);

//     if (Number.isNaN(date.getTime())) {
//         return safeString(value);
//     }

//     return date.toLocaleTimeString("en-LR", {
//         hour: "2-digit",
//         minute: "2-digit",
//         second: "2-digit",
//         hour12: true,
//     });
// };

// const getApplicantName = (data) => {
//     if (data?.applicant_name) {
//         return String(data.applicant_name);
//     }

//     const application = data?.application || {};

//     if (application?.applicant_name) {
//         return String(application.applicant_name);
//     }

//     if (application?.full_name) {
//         return String(application.full_name);
//     }

//     if (application?.name) {
//         return String(application.name);
//     }

//     return [
//         application?.first_name,
//         application?.given_name,
//         application?.middle_name,
//         application?.other_name,
//         application?.last_name,
//         application?.surname,
//     ]
//         .filter(Boolean)
//         .join(" ")
//         .trim() || "Applicant";
// };

// const getApplicationValue = (
//     data,
//     names,
//     fallback = "—"
// ) => {
//     const application = data?.application || {};

//     for (const name of names) {
//         if (
//             application[name] !== null &&
//             application[name] !== undefined &&
//             String(application[name]).trim() !== ""
//         ) {
//             return String(application[name]);
//         }
//     }

//     for (const name of names) {
//         if (
//             data[name] !== null &&
//             data[name] !== undefined &&
//             String(data[name]).trim() !== ""
//         ) {
//             return String(data[name]);
//         }
//     }

//     return fallback;
// };

// const getAttemptAnswers = (attempt) => {
//     if (!attempt) {
//         return [];
//     }

//     const possibleArrays = [
//         attempt.answers,
//         attempt.answer_sheet,
//         attempt.question_answers,
//         attempt.attempt_answers,
//         attempt.responses,
//         attempt.results,
//         attempt.data,
//     ];

//     for (const value of possibleArrays) {
//         if (Array.isArray(value)) {
//             return value;
//         }
//     }

//     return [];
// };

// const normalizeAttemptAnswers = (attempt) => {
//     return getAttemptAnswers(attempt)
//         .map((answer, index) => {
//             const selected = String(
//                 answer?.selected_answer ??
//                     answer?.selectedAnswer ??
//                     ""
//             )
//                 .trim()
//                 .toUpperCase();

//             const correct = String(
//                 answer?.correct_answer ??
//                     answer?.correctAnswer ??
//                     ""
//             )
//                 .trim()
//                 .toUpperCase();

//             const questionNumber = numberValue(
//                 answer?.question_number ??
//                     answer?.questionNumber ??
//                     answer?.question?.question_number,
//                 index + 1
//             );

//             const marks = numberValue(
//                 answer?.marks ??
//                     answer?.question?.marks,
//                 1
//             );

//             const marksAwarded = numberValue(
//                 answer?.marks_awarded ??
//                     answer?.marksAwarded,
//                 selected && selected === correct
//                     ? marks
//                     : 0
//             );

//             /*
//              * IMPORTANT:
//              *
//              * Do NOT use the backend is_correct value
//              * to determine which option was selected.
//              *
//              * The actual selected option MUST come from
//              * selected_answer.
//              *
//              * This prevents the PDF from marking every
//              * correct answer as if the applicant selected it.
//              */
//             const derivedIsCorrect =
//                 Boolean(
//                     selected &&
//                         correct &&
//                         selected === correct
//                 );

//             const optionsSource =
//                 answer?.options ||
//                 answer?.question?.options ||
//                 {};

//             return {
//                 id:
//                     answer?.question_id ??
//                     answer?.question?.id ??
//                     `${attempt?.id || "attempt"}-${questionNumber}`,

//                 number: questionNumber,

//                 section: safeString(
//                     answer?.section ??
//                         answer?.question?.section,
//                     ""
//                 ),

//                 text: safeString(
//                     answer?.question_text ??
//                         answer?.question?.question_text ??
//                         answer?.text,
//                     "Question text unavailable."
//                 ),

//                 options: {
//                     A: safeString(
//                         optionsSource?.A ??
//                             answer?.option_a ??
//                             answer?.question?.option_a,
//                         "—"
//                     ),
//                     B: safeString(
//                         optionsSource?.B ??
//                             answer?.option_b ??
//                             answer?.question?.option_b,
//                         "—"
//                     ),
//                     C: safeString(
//                         optionsSource?.C ??
//                             answer?.option_c ??
//                             answer?.question?.option_c,
//                         "—"
//                     ),
//                     D: safeString(
//                         optionsSource?.D ??
//                             answer?.option_d ??
//                             answer?.question?.option_d,
//                         "—"
//                     ),
//                 },

//                 marks,
//                 marksAwarded,
//                 selected,
//                 correct,

//                 /*
//                  * This is derived directly from:
//                  *
//                  * selected_answer === correct_answer
//                  */
//                 isCorrect: derivedIsCorrect,
//             };
//         })
//         .sort(
//             (a, b) => a.number - b.number
//         );
// };

// const getAttempt = (data, subject) => {
//     const direct = data?.[subject];

//     if (direct) {
//         return direct;
//     }

//     const subjectResult =
//         data?.results?.[subject];

//     if (subjectResult) {
//         return subjectResult;
//     }

//     const attempts =
//         data?.attempts ||
//         data?.exam_attempts;

//     if (Array.isArray(attempts)) {
//         return (
//             attempts.find(
//                 (attempt) =>
//                     String(
//                         attempt?.subject ||
//                             attempt?.exam_subject ||
//                             attempt?.exam?.subject ||
//                             ""
//                     ).toLowerCase() ===
//                     subject.toLowerCase()
//             ) || null
//         );
//     }

//     return null;
// };

// const SubjectPaper = ({
//     title,
//     attempt,
//     subjectNumber,
// }) => {
//     const questions =
//         normalizeAttemptAnswers(attempt);

//     if (!attempt) {
//         return (
//             <View>
//                 <View style={styles.subjectHeader}>
//                     <View style={styles.subjectHeaderLeft}>
//                         <Text
//                             style={
//                                 styles.subjectHeaderTitle
//                             }
//                         >
//                             {title}
//                         </Text>

//                         <Text
//                             style={
//                                 styles.subjectHeaderSubtitle
//                             }
//                         >
//                             No examination attempt was
//                             found for this subject.
//                         </Text>
//                     </View>

//                     <Text
//                         style={styles.subjectScore}
//                     >
//                         —
//                     </Text>
//                 </View>
//             </View>
//         );
//     }

//     const totalMarks = numberValue(
//         attempt?.total_marks ??
//             attempt?.exam_total_marks ??
//             attempt?.total,
//         DEFAULT_TOTAL_MARKS
//     );

//     const obtainedMarks = numberValue(
//         attempt?.obtained_marks ??
//             attempt?.score ??
//             attempt?.marks_obtained,
//         0
//     );

//     return (
//         <View>
//             <View style={styles.subjectHeader}>
//                 <View style={styles.subjectHeaderLeft}>
//                     <Text
//                         style={
//                             styles.subjectHeaderTitle
//                         }
//                     >
//                         {title}
//                     </Text>

//                     <Text
//                         style={
//                             styles.subjectHeaderSubtitle
//                         }
//                     >
//                         Applicant response paper •{" "}
//                         {questions.length} questions
//                     </Text>
//                 </View>

//                 <Text style={styles.subjectScore}>
//                     {formatNumber(
//                         obtainedMarks,
//                         2
//                     )}
//                     /
//                     {formatNumber(
//                         totalMarks,
//                         2
//                     )}
//                 </Text>
//             </View>

//             {questions.length === 0 ? (
//                 <View
//                     style={{
//                         padding: 15,
//                         borderWidth: 1,
//                         borderColor: COLORS.border,
//                         borderRadius: 6,
//                         backgroundColor: COLORS.lightPanel,
//                     }}
//                 >
//                     <Text
//                         style={{
//                             color: COLORS.muted,
//                             fontSize: 7,
//                         }}
//                     >
//                         No question responses were
//                         returned for this examination
//                         attempt.
//                     </Text>
//                 </View>
//             ) : (
//                 questions.map(
//                     (question, index) => {
//                         const selected =
//                             question.selected;

//                         const correct =
//                             question.correct;

//                         const unanswered =
//                             !selected;

//                         return (
//                             /*
//                              * wrap={false} is IMPORTANT.
//                              *
//                              * It prevents one question and
//                              * its options from being split
//                              * between PDF pages.
//                              */
//                             <View
//                                 key={
//                                     question.id ||
//                                     `${attempt?.id || title}-${index}`
//                                 }
//                                 style={
//                                     styles.questionCard
//                                 }
//                                 wrap={false}
//                             >
//                                 <View
//                                     style={
//                                         styles.questionTop
//                                     }
//                                 >
//                                     <View
//                                         style={
//                                             styles.questionNumberWrap
//                                         }
//                                     >
//                                         <View
//                                             style={
//                                                 styles.questionNumberBadge
//                                             }
//                                         >
//                                             <Text
//                                                 style={
//                                                     styles.questionNumberText
//                                                 }
//                                             >
//                                                 Q
//                                                 {
//                                                     question.number
//                                                 }
//                                             </Text>
//                                         </View>

//                                         {question.section && (
//                                             <Text
//                                                 style={
//                                                     styles.questionSection
//                                                 }
//                                             >
//                                                 {
//                                                     question.section
//                                                 }
//                                             </Text>
//                                         )}
//                                     </View>

//                                     <Text
//                                         style={
//                                             styles.questionMarks
//                                         }
//                                     >
//                                         {formatNumber(
//                                             question.marks,
//                                             2
//                                         )}{" "}
//                                         mark
//                                         {question.marks !==
//                                         1
//                                             ? "s"
//                                             : ""}
//                                     </Text>
//                                 </View>

//                                 <Text
//                                     style={
//                                         styles.questionText
//                                     }
//                                 >
//                                     {
//                                         question.text
//                                     }
//                                 </Text>

//                                 {[
//                                     "A",
//                                     "B",
//                                     "C",
//                                     "D",
//                                 ].map(
//                                     (option) => {
//                                         /*
//                                          * THESE THREE CONDITIONS
//                                          * CONTROL THE ENTIRE
//                                          * ANSWER DISPLAY.
//                                          */
//                                         const isSelected =
//                                             selected ===
//                                             option;

//                                         const isCorrectOption =
//                                             correct ===
//                                             option;

//                                         const selectedCorrect =
//                                             isSelected &&
//                                             isCorrectOption;

//                                         const selectedWrong =
//                                             isSelected &&
//                                             !isCorrectOption;

//                                         const correctNotSelected =
//                                             !isSelected &&
//                                             isCorrectOption;

//                                         let optionStyle =
//                                             {
//                                                 ...styles.option,
//                                             };

//                                         let letterCircleStyle =
//                                             {
//                                                 ...styles.optionLetterCircle,
//                                             };

//                                         let letterStyle =
//                                             {
//                                                 ...styles.optionLetter,
//                                             };

//                                         if (
//                                             selectedCorrect
//                                         ) {
//                                             optionStyle =
//                                                 {
//                                                     ...optionStyle,
//                                                     ...styles.optionSelectedCorrect,
//                                                 };

//                                             letterCircleStyle =
//                                                 {
//                                                     ...letterCircleStyle,
//                                                     ...styles.optionLetterCircleSelectedCorrect,
//                                                 };

//                                             letterStyle =
//                                                 {
//                                                     ...letterStyle,
//                                                     ...styles.optionLetterWhite,
//                                                 };
//                                         } else if (
//                                             selectedWrong
//                                         ) {
//                                             optionStyle =
//                                                 {
//                                                     ...optionStyle,
//                                                     ...styles.optionSelectedWrong,
//                                                 };

//                                             letterCircleStyle =
//                                                 {
//                                                     ...letterCircleStyle,
//                                                     ...styles.optionLetterCircleSelectedWrong,
//                                                 };

//                                             letterStyle =
//                                                 {
//                                                     ...letterStyle,
//                                                     ...styles.optionLetterWhite,
//                                                 };
//                                         } else if (
//                                             correctNotSelected
//                                         ) {
//                                             optionStyle =
//                                                 {
//                                                     ...optionStyle,
//                                                     ...styles.optionCorrectNotSelected,
//                                                 };

//                                             letterCircleStyle =
//                                                 {
//                                                     ...letterCircleStyle,
//                                                     ...styles.optionLetterCircleCorrectNotSelected,
//                                                 };
//                                         }

//                                         return (
//                                             <View
//                                                 key={
//                                                     option
//                                                 }
//                                                 style={
//                                                     optionStyle
//                                                 }
//                                             >
//                                                 <View
//                                                     style={
//                                                         letterCircleStyle
//                                                     }
//                                                 >
//                                                     <Text
//                                                         style={
//                                                             letterStyle
//                                                         }
//                                                     >
//                                                         {
//                                                             option
//                                                         }
//                                                     </Text>
//                                                 </View>

//                                                 <Text
//                                                     style={
//                                                         styles.optionText
//                                                     }
//                                                 >
//                                                     {
//                                                         question
//                                                             .options[
//                                                             option
//                                                         ]
//                                                     }
//                                                 </Text>

//                                                 {/*
//                                                  * EXACT INDICATOR RULES:
//                                                  *
//                                                  * Selected + correct:
//                                                  * green check
//                                                  *
//                                                  * Selected + wrong:
//                                                  * red dot
//                                                  *
//                                                  * Correct but not selected:
//                                                  * dim gray dot
//                                                  *
//                                                  * Everything else:
//                                                  * nothing
//                                                  */}
//                                                 <View
//                                                     style={
//                                                         styles.indicatorWrap
//                                                     }
//                                                 >
//                                                     {selectedCorrect && (
//                                                         <Text
//                                                             style={
//                                                                 styles.checkIndicator
//                                                             }
//                                                         >
//                                                             ✓
//                                                         </Text>
//                                                     )}

//                                                     {selectedWrong && (
//                                                         <View
//                                                             style={{
//                                                                 ...styles.dotIndicator,
//                                                                 ...styles.redDot,
//                                                             }}
//                                                         />
//                                                     )}

//                                                     {correctNotSelected && (
//                                                         <View
//                                                             style={{
//                                                                 ...styles.dotIndicator,
//                                                                 ...styles.grayDot,
//                                                             }}
//                                                         />
//                                                     )}
//                                                 </View>
//                                             </View>
//                                         );
//                                     }
//                                 )}

//                                 <View
//                                     style={
//                                         styles.questionResultBar
//                                     }
//                                 >
//                                     <Text
//                                         style={
//                                             styles.questionResultText
//                                         }
//                                     >
//                                         Selected:{" "}
//                                         <Text
//                                             style={
//                                                 styles.questionResultStrong
//                                             }
//                                         >
//                                             {selected ||
//                                                 "Not Answered"}
//                                         </Text>

//                                         {"   "}•{"   "}

//                                         Correct:{" "}
//                                         <Text
//                                             style={
//                                                 styles.questionResultStrong
//                                             }
//                                         >
//                                             {correct ||
//                                                 "—"}
//                                         </Text>
//                                     </Text>

//                                     <View
//                                         style={{
//                                             ...styles.resultChip,
//                                             ...(question.isCorrect
//                                                 ? styles.resultChipCorrect
//                                                 : unanswered
//                                                 ? styles.resultChipUnanswered
//                                                 : styles.resultChipWrong),
//                                         }}
//                                     >
//                                         <Text
//                                             style={{
//                                                 ...styles.resultChipText,
//                                                 color:
//                                                     question.isCorrect
//                                                         ? COLORS.green
//                                                         : unanswered
//                                                         ? COLORS.muted
//                                                         : COLORS.red,
//                                             }}
//                                         >
//                                             {question.isCorrect
//                                                 ? "Correct"
//                                                 : unanswered
//                                                 ? "Not Answered"
//                                                 : "Incorrect"}
//                                         </Text>
//                                     </View>
//                                 </View>

//                                 <View
//                                     style={{
//                                         marginTop: 5,
//                                         flexDirection:
//                                             "row",
//                                         justifyContent:
//                                             "flex-end",
//                                     }}
//                                 >
//                                     <Text
//                                         style={
//                                             styles.questionResultText
//                                         }
//                                     >
//                                         Marks awarded:{" "}
//                                         <Text
//                                             style={
//                                                 styles.questionResultStrong
//                                             }
//                                         >
//                                             {formatNumber(
//                                                 question.marksAwarded,
//                                                 2
//                                             )}
//                                             /
//                                             {formatNumber(
//                                                 question.marks,
//                                                 2
//                                             )}
//                                         </Text>
//                                     </Text>
//                                 </View>
//                             </View>
//                         );
//                     }
//                 )
//             )}
//         </View>
//     );
// };

// const EntrancePapperPDF = ({
//     data = {},
//     schoolName =
//         "PASSION FOR ACADEMIC EXCELLENCE",
//     schoolAddress =
//         "Monrovia, Liberia",
//     schoolContacts =
//         "Phone: — | Email: —",
// }) => {
//     /*
//      * Your EntrancePapper.js already sends:
//      *
//      * <EntrancePapperPDF data={data} />
//      *
//      * So this component intentionally uses `data`
//      * directly.
//      */

//     const mathematics =
//         getAttempt(data, "mathematics");

//     const english =
//         getAttempt(data, "english");

//     const mathQuestions =
//         normalizeAttemptAnswers(
//             mathematics
//         );

//     const englishQuestions =
//         normalizeAttemptAnswers(
//             english
//         );

//     const mathScore = numberValue(
//         data?.math_score ??
//             mathematics?.obtained_marks ??
//             mathematics?.score,
//         0
//     );

//     const englishScore = numberValue(
//         data?.english_score ??
//             english?.obtained_marks ??
//             english?.score,
//         0
//     );

//     const mathTotal = numberValue(
//         mathematics?.total_marks ??
//             mathematics?.exam_total_marks ??
//             mathematics?.total,
//         DEFAULT_TOTAL_MARKS
//     );

//     const englishTotal = numberValue(
//         english?.total_marks ??
//             english?.exam_total_marks ??
//             english?.total,
//         DEFAULT_TOTAL_MARKS
//     );

//     const hasMath = Boolean(
//         mathematics &&
//             mathematics.status !==
//                 "in_progress"
//     );

//     const hasEnglish = Boolean(
//         english &&
//             english.status !==
//                 "in_progress"
//     );

//     /*
//      * Overall entrance score:
//      *
//      * Mathematics + English / 2
//      *
//      * Example:
//      * 37 + 50 = 87 / 2 = 43.50
//      */
//     const finalAverage =
//         data?.average_score !== null &&
//         data?.average_score !== undefined
//             ? numberValue(
//                   data.average_score
//               )
//             : hasMath && hasEnglish
//             ? (mathScore +
//                   englishScore) /
//               2
//             : 0;

//     const finalTotal =
//         DEFAULT_TOTAL_MARKS;

//     const passed =
//         hasMath &&
//         hasEnglish &&
//         finalAverage >= PASS_MARK;

//     const mathCorrect = numberValue(
//         mathematics?.correct_answers,
//         mathQuestions.filter(
//             (question) =>
//                 question.isCorrect
//         ).length
//     );

//     const englishCorrect =
//         numberValue(
//             english?.correct_answers,
//             englishQuestions.filter(
//                 (question) =>
//                     question.isCorrect
//             ).length
//         );

//     const mathWrong = numberValue(
//         mathematics?.wrong_answers,
//         mathQuestions.filter(
//             (question) =>
//                 question.selected &&
//                 !question.isCorrect
//         ).length
//     );

//     const englishWrong =
//         numberValue(
//             english?.wrong_answers,
//             englishQuestions.filter(
//                 (question) =>
//                     question.selected &&
//                     !question.isCorrect
//             ).length
//         );

//     const mathUnanswered =
//         numberValue(
//             mathematics?.unanswered_questions,
//             mathQuestions.filter(
//                 (question) =>
//                     !question.selected
//             ).length
//         );

//     const englishUnanswered =
//         numberValue(
//             english?.unanswered_questions,
//             englishQuestions.filter(
//                 (question) =>
//                     !question.selected
//             ).length
//         );

//     const totalCorrect =
//         mathCorrect + englishCorrect;

//     const totalWrong =
//         mathWrong + englishWrong;

//     const totalUnanswered =
//         mathUnanswered +
//         englishUnanswered;

//     const mathQuestionCount =
//         mathQuestions.length;

//     const englishQuestionCount =
//         englishQuestions.length;

//     const totalQuestions =
//         mathQuestionCount +
//         englishQuestionCount;

//     const percentage =
//         finalTotal > 0
//             ? (finalAverage /
//                   finalTotal) *
//               100
//             : 0;

//     const mathStart =
//         mathematics?.started_at;

//     const mathEnd =
//         mathematics?.submitted_at;

//     const englishStart =
//         english?.started_at;

//     const englishEnd =
//         english?.submitted_at;

//     const firstExamDate =
//         mathStart ||
//         englishStart;

//     const applicationNumber =
//         data?.application_number ||
//         data?.applicationNumber ||
//         data?.application
//             ?.application_number ||
//         data?.application
//             ?.applicationNumber;

//     const applicantName =
//         getApplicantName(data);

//     return (
//         <Document
//             title={`${applicantName} - Entrance Examination`}
//             author={schoolName}
//             subject="Official Entrance Examination Record"
//         >
//             {/* =====================================================
//                 PAGE 1 — APPLICANT SUMMARY
//             ====================================================== */}
//             <Page
//                 size="A4"
//                 style={styles.page}
//                 wrap
//             >
//                 <View style={styles.header}>
//                     <View
//                         style={
//                             styles.headerAccent
//                         }
//                     />

//                     <View
//                         style={
//                             styles.headerTop
//                         }
//                     >
//                         <View
//                             style={
//                                 styles.logoWrap
//                             }
//                         >
//                             <Image
//                                 src={schoolLogo}
//                                 style={
//                                     styles.logo
//                                 }
//                             />
//                         </View>

//                         <View
//                             style={
//                                 styles.schoolBlock
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.schoolName
//                                 }
//                             >
//                                 {schoolName}
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.schoolSubtitle
//                                 }
//                             >
//                                 Office of Admissions •
//                                 Entrance Examination
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.schoolContact
//                                 }
//                             >
//                                 {schoolAddress}
//                                 {"\n"}
//                                 {schoolContacts}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.documentBadge
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.documentBadgeTop
//                                 }
//                             >
//                                 Official Record
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.documentBadgeMain
//                                 }
//                             >
//                                 Entrance
//                                 {"\n"}
//                                 Examination
//                             </Text>
//                         </View>
//                     </View>

//                     <Text
//                         style={
//                             styles.documentTitle
//                         }
//                     >
//                         Applicant Entrance Test Paper
//                     </Text>

//                     <Text
//                         style={
//                             styles.documentSubtitle
//                         }
//                     >
//                         Official completed response,
//                         marking and assessment record
//                     </Text>
//                 </View>

//                 <View
//                     style={
//                         styles.informationHeader
//                     }
//                 >
//                     <View
//                         style={
//                             styles.informationHeaderLine
//                         }
//                     />

//                     <Text
//                         style={
//                             styles.informationHeaderText
//                         }
//                     >
//                         Candidate Information
//                     </Text>

//                     <View
//                         style={
//                             styles.informationHeaderLine
//                         }
//                     />
//                 </View>

//                 <View
//                     style={
//                         styles.applicantBox
//                     }
//                 >
//                     <View
//                         style={
//                             styles.applicantTopBand
//                         }
//                     />

//                     <View
//                         style={
//                             styles.applicantGrid
//                         }
//                     >
//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Applicant Name
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {applicantName}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Application Number
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {safeString(
//                                     applicationNumber
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Academic Year
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     [
//                                         "academic_year",
//                                         "academicYear",
//                                         "year",
//                                     ]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 First Name
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     [
//                                         "first_name",
//                                         "given_name",
//                                     ]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Last Name
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     [
//                                         "last_name",
//                                         "surname",
//                                     ]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Email
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     ["email"]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Phone
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     [
//                                         "phone",
//                                         "phone_number",
//                                         "telephone",
//                                         "mobile",
//                                     ]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Program
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {getApplicationValue(
//                                     data,
//                                     [
//                                         "program",
//                                         "program_name",
//                                         "programme",
//                                     ]
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Examination Date
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {formatDate(
//                                     firstExamDate
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 Mathematics Time
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {formatTime(
//                                     mathStart
//                                 )}{" "}
//                                 —{" "}
//                                 {formatTime(
//                                     mathEnd
//                                 )}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.applicantItem
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.applicantLabel
//                                 }
//                             >
//                                 English Time
//                             </Text>

//                             <Text
//                                 style={
//                                     styles.applicantValue
//                                 }
//                             >
//                                 {formatTime(
//                                     englishStart
//                                 )}{" "}
//                                 —{" "}
//                                 {formatTime(
//                                     englishEnd
//                                 )}
//                             </Text>
//                         </View>
//                     </View>
//                 </View>

//                 <View
//                     style={
//                         styles.performanceArea
//                     }
//                 >
//                     <Text
//                         style={
//                             styles.performanceTitle
//                         }
//                     >
//                         Examination Performance
//                     </Text>

//                     <View
//                         style={
//                             styles.performanceRow
//                         }
//                     >
//                         <View
//                             style={
//                                 styles.scorePanel
//                             }
//                         >
//                             <View
//                                 style={
//                                     styles.scoreCircleOuter
//                                 }
//                             >
//                                 <View
//                                     style={
//                                         styles.scoreCircleInner
//                                     }
//                                 >
//                                     <View
//                                         style={
//                                             styles.scoreNumberRow
//                                         }
//                                     >
//                                         <Text
//                                             style={
//                                                 passed
//                                                     ? styles.scoreNumberPassed
//                                                     : styles.scoreNumberFailed
//                                             }
//                                         >
//                                             {formatNumber(
//                                                 finalAverage,
//                                                 2
//                                             )}
//                                         </Text>

//                                         <Text
//                                             style={
//                                                 styles.scoreSlash
//                                             }
//                                         >
//                                             /
//                                         </Text>

//                                         <Text
//                                             style={
//                                                 styles.scoreTotal
//                                             }
//                                         >
//                                             {finalTotal}
//                                         </Text>
//                                     </View>

//                                     <Text
//                                         style={
//                                             styles.scoreOf
//                                         }
//                                     >
//                                         Final Raw Average
//                                     </Text>
//                                 </View>
//                             </View>

//                             <Text
//                                 style={
//                                     styles.scoreCaption
//                                 }
//                             >
//                                 Final Examination Score
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.scorePanels
//                             }
//                         >
//                             <View
//                                 style={{
//                                     ...styles.metricCard,
//                                     ...styles.metricCardTopLeft,
//                                 }}
//                             >
//                                 <Text
//                                     style={
//                                         styles.metricLabel
//                                     }
//                                 >
//                                     Mathematics
//                                 </Text>

//                                 <Text
//                                     style={{
//                                         ...styles.metricValue,
//                                         color: COLORS.blue,
//                                     }}
//                                 >
//                                     {formatNumber(
//                                         mathScore,
//                                         2
//                                     )}
//                                     /
//                                     {formatNumber(
//                                         mathTotal,
//                                         2
//                                     )}
//                                 </Text>
//                             </View>

//                             <View
//                                 style={{
//                                     ...styles.metricCard,
//                                     ...styles.metricCardTopRight,
//                                 }}
//                             >
//                                 <Text
//                                     style={
//                                         styles.metricLabel
//                                     }
//                                 >
//                                     English
//                                 </Text>

//                                 <Text
//                                     style={{
//                                         ...styles.metricValue,
//                                         color: COLORS.blue,
//                                     }}
//                                 >
//                                     {formatNumber(
//                                         englishScore,
//                                         2
//                                     )}
//                                     /
//                                     {formatNumber(
//                                         englishTotal,
//                                         2
//                                     )}
//                                 </Text>
//                             </View>

//                             <View
//                                 style={{
//                                     ...styles.metricCard,
//                                     ...styles.metricCardBottomLeft,
//                                 }}
//                             >
//                                 <Text
//                                     style={
//                                         styles.metricLabel
//                                     }
//                                 >
//                                     Correct Answers
//                                 </Text>

//                                 <Text
//                                     style={{
//                                         ...styles.metricValue,
//                                         color: COLORS.green,
//                                     }}
//                                 >
//                                     {totalCorrect}
//                                 </Text>
//                             </View>

//                             <View
//                                 style={{
//                                     ...styles.metricCard,
//                                     ...styles.metricCardBottomRight,
//                                 }}
//                             >
//                                 <Text
//                                     style={
//                                         styles.metricLabel
//                                     }
//                                 >
//                                     Incorrect / Unanswered
//                                 </Text>

//                                 <Text
//                                     style={{
//                                         ...styles.metricValue,
//                                         color: COLORS.red,
//                                     }}
//                                 >
//                                     {totalWrong} /{" "}
//                                     {totalUnanswered}
//                                 </Text>
//                             </View>
//                         </View>
//                     </View>

//                     <View
//                         style={{
//                             ...styles.statusPanel,
//                             ...(passed
//                                 ? styles.statusPassed
//                                 : styles.statusFailed),
//                         }}
//                     >
//                         <View
//                             style={{
//                                 ...styles.statusIcon,
//                                 ...(passed
//                                     ? styles.statusIconPassed
//                                     : styles.statusIconFailed),
//                             }}
//                         >
//                             <Text
//                                 style={
//                                     styles.statusIconText
//                                 }
//                             >
//                                 {passed
//                                     ? "✓"
//                                     : "!"}
//                             </Text>
//                         </View>

//                         <View
//                             style={
//                                 styles.statusContent
//                             }
//                         >
//                             <Text
//                                 style={{
//                                     ...styles.statusTitle,
//                                     color: passed
//                                         ? COLORS.green
//                                         : COLORS.red,
//                                 }}
//                             >
//                                 {passed
//                                     ? "PASSED / SUCCESSFUL"
//                                     : "FAILED"}
//                             </Text>

//                             <Text
//                                 style={{
//                                     ...styles.statusDescription,
//                                     color: passed
//                                         ? "#39733B"
//                                         : "#8C4141",
//                                 }}
//                             >
//                                 Final raw average:{" "}
//                                 {formatNumber(
//                                     finalAverage,
//                                     2
//                                 )}
//                                 /{finalTotal}. Minimum
//                                 passing raw average:{" "}
//                                 {PASS_MARK}/{finalTotal}.
//                             </Text>
//                         </View>
//                     </View>
//                 </View>

//                 <View
//                     style={styles.legendBox}
//                 >
//                     <Text
//                         style={
//                             styles.legendTitle
//                         }
//                     >
//                         Answer Identification
//                     </Text>

//                     <View
//                         style={
//                             styles.legendRow
//                         }
//                     >
//                         <View
//                             style={
//                                 styles.legendCheck
//                             }
//                         >
//                             <Text
//                                 style={
//                                     styles.checkIndicator
//                                 }
//                             >
//                                 ✓
//                             </Text>
//                         </View>

//                         <Text
//                             style={
//                                 styles.legendText
//                             }
//                         >
//                             Green check — the applicant
//                             selected the correct answer.
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.legendRow
//                         }
//                     >
//                         <View
//                             style={{
//                                 ...styles.legendDot,
//                                 backgroundColor:
//                                     COLORS.red,
//                             }}
//                         />

//                         <Text
//                             style={
//                                 styles.legendText
//                             }
//                         >
//                             Red dot — the applicant
//                             selected this incorrect answer.
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.legendRow
//                         }
//                     >
//                         <View
//                             style={{
//                                 ...styles.legendDot,
//                                 backgroundColor:
//                                     COLORS.grayDot,
//                             }}
//                         />

//                         <Text
//                             style={
//                                 styles.legendText
//                             }
//                         >
//                             Dim gray dot — this is the
//                             correct answer, but it was not
//                             selected by the applicant.
//                         </Text>
//                     </View>
//                 </View>

//                 <Text
//                     style={{
//                         ...styles.watermark,
//                         color: passed
//                             ? COLORS.blue
//                             : COLORS.red,
//                     }}
//                     fixed
//                 >
//                     {passed
//                         ? "PASSED • SUCCESSFUL"
//                         : "FAILED • OFFICIAL RECORD"}
//                 </Text>

//                 <View
//                     style={
//                         styles.pageFooter
//                     }
//                     fixed
//                 >
//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                     >
//                         {schoolName}
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterCenter
//                         }
//                     >
//                         Official Entrance Examination Record
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                         render={({
//                             pageNumber,
//                             totalPages,
//                         }) =>
//                             `Page ${pageNumber} of ${totalPages}`
//                         }
//                     />
//                 </View>
//             </Page>

//             {/* =====================================================
//                 PAGE 2+ — MATHEMATICS
//             ====================================================== */}
//             <Page
//                 size="A4"
//                 style={styles.page}
//                 wrap
//             >
//                 <View
//                     style={
//                         styles.sectionHeading
//                     }
//                 >
//                     <View
//                         style={
//                             styles.sectionHeadingNumber
//                         }
//                     >
//                         <Text
//                             style={
//                                 styles.sectionHeadingNumberText
//                             }
//                         >
//                             01
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.sectionHeadingTextWrap
//                         }
//                     >
//                         <Text
//                             style={
//                                 styles.sectionHeadingTitle
//                             }
//                         >
//                             Mathematics Answer Paper
//                         </Text>

//                         <Text
//                             style={
//                                 styles.sectionHeadingSubtitle
//                             }
//                         >
//                             Applicant's actual selected
//                             answers and official marking
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.sectionHeadingLine
//                         }
//                     />
//                 </View>

//                 <SubjectPaper
//                     title="Mathematics"
//                     attempt={mathematics}
//                     subjectNumber={1}
//                 />

//                 <Text
//                     style={{
//                         ...styles.watermark,
//                         color: passed
//                             ? COLORS.blue
//                             : COLORS.red,
//                     }}
//                     fixed
//                 >
//                     {passed
//                         ? "PASSED • SUCCESSFUL"
//                         : "FAILED • OFFICIAL RECORD"}
//                 </Text>

//                 <View
//                     style={
//                         styles.pageFooter
//                     }
//                     fixed
//                 >
//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                     >
//                         {schoolName}
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterCenter
//                         }
//                     >
//                         Mathematics • Entrance Examination
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                         render={({
//                             pageNumber,
//                             totalPages,
//                         }) =>
//                             `Page ${pageNumber} of ${totalPages}`
//                         }
//                     />
//                 </View>
//             </Page>

//             {/* =====================================================
//                 ENGLISH — NEW PAGE
//             ====================================================== */}
//             <Page
//                 size="A4"
//                 style={styles.page}
//                 wrap
//             >
//                 <View
//                     style={
//                         styles.sectionHeading
//                     }
//                 >
//                     <View
//                         style={
//                             styles.sectionHeadingNumber
//                         }
//                     >
//                         <Text
//                             style={
//                                 styles.sectionHeadingNumberText
//                             }
//                         >
//                             02
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.sectionHeadingTextWrap
//                         }
//                     >
//                         <Text
//                             style={
//                                 styles.sectionHeadingTitle
//                             }
//                         >
//                             English Answer Paper
//                         </Text>

//                         <Text
//                             style={
//                                 styles.sectionHeadingSubtitle
//                             }
//                         >
//                             Applicant's actual selected
//                             answers and official marking
//                         </Text>
//                     </View>

//                     <View
//                         style={
//                             styles.sectionHeadingLine
//                         }
//                     />
//                 </View>

//                 <SubjectPaper
//                     title="English"
//                     attempt={english}
//                     subjectNumber={2}
//                 />

//                 <Text
//                     style={{
//                         ...styles.watermark,
//                         color: passed
//                             ? COLORS.blue
//                             : COLORS.red,
//                     }}
//                     fixed
//                 >
//                     {passed
//                         ? "PASSED • SUCCESSFUL"
//                         : "FAILED • OFFICIAL RECORD"}
//                 </Text>

//                 <View
//                     style={
//                         styles.pageFooter
//                     }
//                     fixed
//                 >
//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                     >
//                         {schoolName}
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterCenter
//                         }
//                     >
//                         English • Entrance Examination
//                     </Text>

//                     <Text
//                         style={
//                             styles.pageFooterText
//                         }
//                         render={({
//                             pageNumber,
//                             totalPages,
//                         }) =>
//                             `Page ${pageNumber} of ${totalPages}`
//                         }
//                     />
//                 </View>
//             </Page>
//         </Document>
//     );
// };

// export default EntrancePapperPDF;

